/* Mimartis — product module: gallery, zoom, variant picker, quantity, 3D/AR.
   Loaded by sections that render a product (main-product, featured-product),
   not part of the global bundle. Every [data-mim-product] root gets its own
   instance, so several products can live on one page (e.g. the homepage).
   Self-registers against window.Mim (mim-theme.js core). */

(function () {
  'use strict';
  if (!window.Mim) return;
  const Mim = window.Mim;

  // Registry: mounts one ProductView per root (also after editor reloads).
  const Product = {
    init() {
      this.views = [];
      Mim.utils.qsa('[data-mim-product]').forEach((root) => this.mount(root));
      document.addEventListener('shopify:section:load', (e) => {
        Mim.utils.qsa('[data-mim-product]', e.target).forEach((root) => this.mount(root));
      });
    },

    mount(root) {
      if (root.dataset.mimProductReady) return;
      root.dataset.mimProductReady = 'true';
      const view = Object.create(ProductView);
      view.setup(root);
      this.views.push(view);
    },
  };

  const ProductView = {
    setup(root) {
      this.root = root;
      // Only the product page reflects the variant in the URL.
      this.syncUrl = root.hasAttribute('data-mim-product-url');

      const data = this.root.querySelector('[data-mim-product-variants]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.idField = this.root.querySelector('[data-mim-variant-id]');
      this.priceEl = this.root.querySelector('[data-mim-price]');
      this.atc = this.root.querySelector('[data-mim-atc]');
      this.atcText = this.root.querySelector('[data-mim-atc-text]');

      this.root.addEventListener('change', (e) => {
        if (!e.target.matches('[data-mim-option-value]')) return;
        // Combined listings: this value is another product → go there.
        if (e.target.dataset.mimProductLink) {
          window.location.href = e.target.dataset.mimProductLink;
          return;
        }
        // Swatch options show the chosen value next to the option name.
        const current = e.target.closest('[data-mim-option]').querySelector('[data-mim-option-current]');
        if (current) current.textContent = e.target.value;
        this.onVariantChange();
      });

      // Gift card recipient: Shopify needs the buyer's timezone offset.
      Mim.utils.qsa('[data-mim-tz-offset]', this.root).forEach((el) => {
        el.value = new Date().getTimezoneOffset().toString();
      });

      this.root.addEventListener('click', (e) => {
        const step = e.target.closest('[data-mim-qty-step]');
        if (step) {
          this.stepQty(parseInt(step.dataset.mimQtyStep, 10));
          return;
        }
        const zoom = e.target.closest('[data-mim-zoom]');
        if (zoom) this.openZoom(zoom);
        const copy = e.target.closest('[data-mim-copy-link]');
        if (copy && navigator.clipboard) {
          navigator.clipboard.writeText(copy.dataset.mimCopyLink).then(() => {
            const label = copy.textContent;
            copy.textContent = copy.dataset.copied;
            setTimeout(() => { copy.textContent = label; }, 2000);
          });
        }
      });

      // Quantity typed by hand → highlight the matching volume tier.
      this.qtyField = this.root.querySelector('[data-mim-qty-field]');
      if (this.qtyField) this.qtyField.addEventListener('input', () => this.syncTiers());

      this.pickup = this.root.querySelector('[data-mim-pickup]');
      this.pickupCache = new Map();
      // The variant rendered server-side is already in the page.
      if (this.pickup && this.idField) this.pickupCache.set(this.idField.value, this.pickup.innerHTML);

      this.fillEta();
      this.syncTiers();
      this.initGallery();
      this.initZoom();
      this.initModels();
      this.initSticky();
    },

    // ── Quantity & variant-dependent blocks ─────────────────────────────────
    // The stepper follows the field's min / max / step (the variant's
    // quantity rule, B2B), so steps land on valid quantities.
    stepQty(dir) {
      const field = this.qtyField;
      if (!field) return;
      const min = parseInt(field.min, 10) || 1;
      const step = parseInt(field.step, 10) || 1;
      const max = parseInt(field.max, 10) || Infinity;
      const current = parseInt(field.value, 10) || min;
      // Snap to the rule's grid (min, min + step, …) before stepping.
      const snapped = min + Math.round((current - min) / step) * step;
      field.value = Math.min(max, Math.max(min, snapped + dir * step));
      this.syncTiers();
    },

    applyQtyRule(rule) {
      const field = this.qtyField;
      if (!field || !rule) return;
      field.min = rule.min;
      field.step = rule.step;
      if (rule.max) field.max = rule.max;
      else field.removeAttribute('max');
      const value = parseInt(field.value, 10) || 0;
      if (value < rule.min) field.value = rule.min;
      if (rule.max && value > rule.max) field.value = rule.max;
    },

    // Blocks rendered per variant (volume pricing, production status) keep a
    // <template> per variant; swap the matching one into view.
    swapVariantViews(id) {
      Mim.utils.qsa('[data-mim-per-variant]', this.root).forEach((block) => {
        const tpl = block.querySelector(`[data-mim-variant-tpl="${id}"]`);
        const view = block.querySelector('[data-mim-variant-view]');
        if (!tpl || !view) return;
        view.replaceChildren(tpl.content.cloneNode(true));
      });
      this.fillEta();
      this.syncTiers();
    },

    // Pickup availability depends on the variant and on the merchant's
    // locations, so it comes from the server (Section Rendering API) instead
    // of a template per variant: store_availabilities for every variant would
    // slow the page down. Answers are cached per variant; a pending request is
    // dropped when the shopper picks another variant.
    loadPickup(id) {
      const holder = this.pickup;
      if (!holder) return;
      const key = String(id);
      const cached = this.pickupCache.get(key);
      if (cached !== undefined) {
        holder.innerHTML = cached;
        return;
      }
      if (this.pickupRequest) this.pickupRequest.abort();
      const request = new AbortController();
      this.pickupRequest = request;
      const root = holder.dataset.mimPickupRoot || '/';
      fetch(`${root}variants/${key}/?section_id=pickup-availability`, { signal: request.signal })
        .then((res) => (res.ok ? res.text() : Promise.reject(res.status)))
        .then((html) => {
          const section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
          if (!section) return;
          const markup = section.innerHTML.trim();
          this.pickupCache.set(key, markup);
          holder.innerHTML = markup;
        })
        .catch(() => {});
    },

    // Volume pricing: mark the row whose range holds the chosen quantity.
    syncTiers() {
      const rows = Mim.utils.qsa('[data-mim-variant-view] [data-mim-tier-min]', this.root);
      if (!rows.length) return;
      const qty = this.qtyField ? parseInt(this.qtyField.value, 10) || 1 : 1;
      let active = null;
      rows.forEach((row) => { if (qty >= parseInt(row.dataset.mimTierMin, 10)) active = row; });
      rows.forEach((row) => row.classList.toggle('is-active', row === active));
    },

    // Delivery estimate: "min,max" business days from today → a date range
    // in the page's language. Done here (not Liquid) so cached pages stay
    // right. Also feeds the made-to-order timeline, whose first step is "0,0"
    // (today) and whose steps carry no label of their own. A pre-order
    // counts from its release day instead (data-mim-eta-from="YYYY-MM-DD").
    fillEta() {
      Mim.utils.qsa('[data-mim-variant-view] [data-mim-eta]', this.root).forEach((el) => {
        if (!el.dataset.mimEta) return;
        const [min, max] = el.dataset.mimEta.split(',').map((n) => parseInt(n, 10) || 0);
        let base = new Date();
        if (el.dataset.mimEtaFrom) {
          const from = new Date(`${el.dataset.mimEtaFrom}T12:00:00`);
          if (!Number.isNaN(from.getTime()) && from > base) base = from;
        }
        const addBusinessDays = (days) => {
          const date = new Date(base);
          let left = days;
          while (left > 0) {
            date.setDate(date.getDate() + 1);
            const day = date.getDay();
            if (day !== 0 && day !== 6) left -= 1;
          }
          return date;
        };
        const lang = document.documentElement.lang || undefined;
        const format = new Intl.DateTimeFormat(lang, { weekday: 'short', day: 'numeric', month: 'short' });
        const from = format.format(addBusinessDays(Math.min(min, max)));
        const to = format.format(addBusinessDays(Math.max(min, max)));
        const range = from === to ? from : `${from} – ${to}`;
        const label = el.dataset.mimEtaLabel || '';
        el.textContent = label ? `${label} ${range}` : range;
        el.hidden = false;
      });
    },

    // ── Sticky add to cart (product page) ───────────────────────────────────
    // Shown while the main add-to-cart button is out of view (scrolled past,
    // not before it). Clicking submits the main form; with options that
    // haven't resolved to an available variant it scrolls back to them.
    initSticky() {
      this.sticky = this.root.querySelector('[data-mim-sticky-atc]');
      if (!this.sticky || !this.atc || !('IntersectionObserver' in window)) return;
      this.stickyButton = this.sticky.querySelector('[data-mim-sticky-button]');
      this.stickyPrice = this.sticky.querySelector('[data-mim-sticky-price]');
      const form = this.atc.form;

      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.sticky.classList.toggle('is-visible', show);
        this.sticky.toggleAttribute('inert', !show);
        this.sticky.setAttribute('aria-hidden', String(!show));
        if (show) document.body.style.setProperty('--mim-sticky-atc-height', `${this.sticky.offsetHeight}px`);
        document.body.classList.toggle('has-sticky-atc', show);
      }).observe(this.atc);

      this.stickyButton.addEventListener('click', () => {
        if (this.atc.disabled) {
          this.atc.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        if (form.requestSubmit) form.requestSubmit(this.atc);
        else this.atc.click();
      });
    },

    syncSticky() {
      if (!this.sticky) return;
      this.stickyButton.disabled = this.atc.disabled;
      this.stickyButton.textContent = this.atcText.textContent;
      if (this.priceEl && this.stickyPrice) {
        const price = this.stickyPrice.querySelector('[data-mim-price]');
        if (price) price.innerHTML = this.priceEl.innerHTML;
      }
    },

    // ── Gallery ──────────────────────────────────────────────────────────────
    // Two modes (section setting): `slide` — the slides row is a native
    // scroll-snap scroller, so swipe/trackpad work natively, the mouse drags
    // it, and the active slide follows the scroll position; `fade` — slides
    // are stacked and the active one cross-fades in on a mouse/touch swipe.
    // Dragging/swiping is the shared drag component (components/mim-drag.js).
    initGallery() {
      const gallery = this.root.querySelector('[data-mim-gallery]');
      if (!gallery) return;
      this.gallery = gallery;
      this.mode = gallery.dataset.mimGalleryMode || 'slide';
      this.track = gallery.querySelector('[data-mim-gallery-slides]');
      this.slides = Mim.utils.qsa('[data-mim-media]', this.track);
      this.thumbs = Mim.utils.qsa('[data-mim-thumb]', gallery);
      this.thumbRow = gallery.querySelector('[data-mim-gallery-thumbs]');
      this.prevBtn = gallery.querySelector('[data-mim-gallery-prev]');
      this.nextBtn = gallery.querySelector('[data-mim-gallery-next]');
      this.counter = gallery.querySelector('[data-mim-gallery-current]');
      this.index = Math.max(0, this.slides.findIndex((el) => el.classList.contains('is-active')));
      this.arButton = gallery.querySelector('[data-mim-ar]');
      if (this.slides.length < 2) return;

      // Desktop grid / one-column layouts: every slide is on the page, so
      // none is inert and "go to" scrolls the page to it instead.
      const layout = gallery.dataset.mimGalleryLayout || 'carousel';
      const laidOut = window.matchMedia('(min-width: 1024px)');
      this.isLaidOut = () => layout !== 'carousel' && laidOut.matches;
      laidOut.addEventListener('change', () => this.setActive(this.index));

      if (this.prevBtn) this.prevBtn.hidden = false;
      if (this.nextBtn) this.nextBtn.hidden = false;

      Mim.utils.on(gallery, 'click', (e) => {
        const thumb = e.target.closest('[data-mim-thumb]');
        if (thumb) this.goTo(this.thumbs.indexOf(thumb));
        else if (e.target.closest('[data-mim-gallery-prev]')) this.goTo(this.index - 1);
        else if (e.target.closest('[data-mim-gallery-next]')) this.goTo(this.index + 1);
      });

      // Left/right arrows while focus is anywhere in the stage.
      Mim.utils.on(this.track.parentElement, 'keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(this.index - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(this.index + 1); }
      });

      if (this.mode === 'slide') {
        // Start on the featured (e.g. variant) slide without animating.
        if (this.index > 0) this.track.scrollLeft = this.index * this.track.clientWidth;
        const sync = Mim.utils.debounce(() => {
          if (this.track.classList.contains('is-gliding')) return;
          const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
          if (i !== this.index) this.setActive(i);
        }, 60);
        Mim.utils.on(this.track, 'scroll', sync, { passive: true });
        // Keep the active slide aligned when the frame width changes.
        window.addEventListener('resize', Mim.utils.debounce(() => {
          this.track.scrollLeft = this.index * this.track.clientWidth;
        }, 100));
      }
      this.initDrag();

      this.setActive(this.index);
    },

    initDrag() {
      const drag = Mim.modules.drag;
      if (!drag) return;
      drag.attach(this.track, {
        mode: this.mode === 'slide' ? 'scroll' : 'swipe',
        index: () => this.index,
        count: () => this.slides.length,
        onSettle: (i) => { if (i !== this.index) this.setActive(i); },
        onSwipe: (dir) => this.goTo(this.index + dir),
      });
    },

    goTo(i) {
      if (!this.slides || i < 0 || i >= this.slides.length) return;
      if (this.isLaidOut && this.isLaidOut()) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const top = this.slides[i].getBoundingClientRect().top;
        // Only scroll when the media isn't already in view.
        if (top < 0 || top > window.innerHeight * 0.6) {
          this.slides[i].scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        }
        this.setActive(i);
        return;
      }
      if (this.mode === 'slide') {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollTo({ left: i * this.track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
      }
      this.setActive(i);
    },

    setActive(i) {
      const previous = this.slides[this.index];
      if (previous && this.index !== i) this.pauseMedia(previous);
      this.index = i;
      const laidOut = this.isLaidOut && this.isLaidOut();
      this.slides.forEach((el, n) => {
        const on = n === i;
        el.classList.toggle('is-active', on);
        el.inert = !on && !laidOut;
      });
      this.syncAr();
      this.thumbs.forEach((el, n) => {
        const on = n === i;
        el.classList.toggle('is-active', on);
        if (on) el.setAttribute('aria-current', 'true');
        else el.removeAttribute('aria-current');
      });
      if (this.counter) this.counter.textContent = String(i + 1);
      if (this.prevBtn) this.prevBtn.disabled = i === 0;
      if (this.nextBtn) this.nextBtn.disabled = i === this.slides.length - 1;

      // Bring the active thumb into view inside its own row (never the page).
      const thumb = this.thumbs[i];
      if (thumb && this.thumbRow) {
        const row = this.thumbRow;
        const left = thumb.parentElement.offsetLeft - row.offsetLeft;
        if (left < row.scrollLeft || left + thumb.offsetWidth > row.scrollLeft + row.clientWidth) {
          row.scrollTo({ left: left - (row.clientWidth - thumb.offsetWidth) / 2, behavior: 'smooth' });
        }
      }
    },

    // Zoom — a lightbox slider over every image of the gallery (3D/video
    // slides are skipped), using the gallery's own transition: `slide` → a
    // scroll-snap row (swipe, mouse drag with release glide), `fade` → a
    // cross-fade (swipe/drag to change). Opens instantly: each slide shows the
    // already-loaded gallery image (or a small thumb) blurred with a spinner
    // while its 2048px file loads, then that fades in. Large files are
    // prefetched on hover/focus of a zoom button and for the neighbours of the
    // image shown. ← / → (buttons, keys) step between images and the gallery
    // follows; only ✕ or Escape close it.
    initZoom() {
      this.overlay = this.root.querySelector('[data-mim-zoom-overlay]');
      if (!this.overlay) return;
      this.zoomTrack = this.overlay.querySelector('[data-mim-zoom-track]');
      this.zoomCount = this.overlay.querySelector('[data-mim-zoom-count]');
      this.zoomClose = this.overlay.querySelector('[data-mim-zoom-close]');
      this.zoomPrev = this.overlay.querySelector('[data-mim-zoom-prev]');
      this.zoomNext = this.overlay.querySelector('[data-mim-zoom-next]');
      this.zoomButtons = Mim.utils.qsa('[data-mim-zoom]', this.root);
      this.zoomSlide = (this.mode || 'slide') === 'slide';
      this.zoomIndex = 0;
      this.prefetched = new Set();
      this.zoomSlides = this.zoomButtons.map((btn) => this.buildZoomSlide(btn));

      // Same furniture as the gallery: arrows, plus counter / thumbnails only
      // when the gallery shows them.
      const multi = this.zoomButtons.length > 1;
      [this.zoomPrev, this.zoomNext].forEach((el) => { if (el) el.hidden = !multi; });
      if (this.zoomCount) this.zoomCount.hidden = !multi || !this.counter;
      this.zoomThumbs = this.buildZoomThumbs(multi && this.thumbRow);

      const prefetch = (e) => {
        const btn = e.target.closest && e.target.closest('[data-mim-zoom]');
        if (btn) this.prefetchZoom(btn.dataset.mimZoom);
      };
      this.root.addEventListener('pointerover', prefetch);
      this.root.addEventListener('focusin', prefetch);

      this.overlay.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-mim-zoom-thumb-index]');
        if (thumb) return this.showZoom(parseInt(thumb.dataset.mimZoomThumbIndex, 10));
        if (e.target.closest('[data-mim-zoom-prev]')) return this.showZoom(this.zoomIndex - 1);
        if (e.target.closest('[data-mim-zoom-next]')) return this.showZoom(this.zoomIndex + 1);
        if (e.target.closest('[data-mim-zoom-close]')) this.closeZoom();
      });
      document.addEventListener('keydown', (e) => {
        if (this.overlay.hidden) return;
        if (e.key === 'Escape') this.closeZoom();
        else if (e.key === 'ArrowLeft') { e.preventDefault(); this.showZoom(this.zoomIndex - 1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); this.showZoom(this.zoomIndex + 1); }
      });

      if (this.zoomSlide) {
        Mim.utils.on(this.zoomTrack, 'scroll', Mim.utils.debounce(() => {
          if (this.zoomTrack.classList.contains('is-gliding') || this.overlay.hidden) return;
          const i = Math.round(this.zoomTrack.scrollLeft / (this.zoomTrack.clientWidth || 1));
          if (i !== this.zoomIndex) this.setZoomActive(i);
        }, 60), { passive: true });
        window.addEventListener('resize', Mim.utils.debounce(() => {
          if (!this.overlay.hidden) this.zoomTrack.scrollLeft = this.zoomIndex * this.zoomTrack.clientWidth;
        }, 100));
      }
      if (multi && Mim.modules.drag) {
        Mim.modules.drag.attach(this.zoomTrack, {
          mode: this.zoomSlide ? 'scroll' : 'swipe',
          index: () => this.zoomIndex,
          count: () => this.zoomButtons.length,
          onSettle: (i) => this.setZoomActive(i),
          onSwipe: (dir) => this.showZoom(this.zoomIndex + dir),
        });
      }
    },

    buildZoomSlide(btn) {
      const slide = document.createElement('div');
      slide.className = 'mim-product__zoom-slide';
      slide.innerHTML =
        '<div class="mim-product__zoom-frame">' +
        '<img class="mim-product__zoom-preview" alt="" aria-hidden="true">' +
        '<img class="mim-product__zoom-image" alt="">' +
        '<span class="mim-product__zoom-loader" aria-hidden="true"></span></div>';
      const frame = slide.firstChild;
      if (btn.dataset.mimZoomRatio) frame.style.setProperty('--mim-zoom-ratio', btn.dataset.mimZoomRatio);
      frame.querySelector('.mim-product__zoom-image').alt = btn.dataset.mimZoomAlt || '';
      this.zoomTrack.appendChild(slide);
      return slide;
    },

    // Clone the gallery's thumb buttons for the image slides into the zoom
    // row, retagged so the gallery's own click handler ignores them.
    buildZoomThumbs(show) {
      const row = this.overlay.querySelector('[data-mim-zoom-thumbs]');
      if (!row || !show) return [];
      row.hidden = false;
      return this.zoomButtons.map((btn, i) => {
        const id = btn.closest('[data-mim-media]')?.dataset.mimMedia;
        const src = this.thumbs.find((t) => t.dataset.mimThumb === id);
        const li = document.createElement('li');
        const b = src ? src.cloneNode(true) : document.createElement('button');
        b.removeAttribute('data-mim-thumb');
        b.removeAttribute('aria-current');
        b.classList.remove('is-active');
        b.type = 'button';
        b.dataset.mimZoomThumbIndex = String(i);
        li.appendChild(b);
        row.appendChild(li);
        return b;
      });
    },

    prefetchZoom(src) {
      if (!src || this.prefetched.has(src)) return;
      this.prefetched.add(src);
      new Image().src = src;
    },

    // Fill slide i (once): preview from the gallery image if loaded, else the
    // small thumb; then the large file, faded in when ready.
    loadZoom(i) {
      const slide = this.zoomSlides[i];
      const btn = this.zoomButtons[i];
      if (!slide || slide.dataset.mimLoaded) return;
      slide.dataset.mimLoaded = 'true';
      const frame = slide.firstChild;
      const preview = frame.querySelector('.mim-product__zoom-preview');
      const img = frame.querySelector('.mim-product__zoom-image');
      const slideImg = btn.closest('[data-mim-media]')?.querySelector('img');
      const loaded = slideImg && slideImg.complete && slideImg.naturalWidth;
      if (!btn.dataset.mimZoomRatio && loaded) frame.style.setProperty('--mim-zoom-ratio', slideImg.naturalWidth / slideImg.naturalHeight);
      preview.src = loaded ? slideImg.currentSrc || slideImg.src : btn.dataset.mimZoomThumb || '';
      img.onload = () => frame.classList.add('is-loaded');
      img.src = btn.dataset.mimZoom;
      if (img.complete && img.naturalWidth) frame.classList.add('is-loaded'); // cached
    },

    openZoom(btn) {
      if (!this.overlay) return;
      const i = Math.max(0, this.zoomButtons.indexOf(btn));
      this.overlay.hidden = false;
      document.body.classList.add('is-drawer-open');
      if (this.zoomSlide) this.zoomTrack.scrollLeft = i * this.zoomTrack.clientWidth;
      this.setZoomActive(i);
      if (this.zoomClose) this.zoomClose.focus({ preventScroll: true });
    },

    // Buttons / keys / fade swipe. Clamped at the ends, like the gallery.
    showZoom(i) {
      if (i < 0 || i >= this.zoomButtons.length || i === this.zoomIndex) return;
      if (this.zoomSlide) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.zoomTrack.scrollTo({ left: i * this.zoomTrack.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
      }
      this.setZoomActive(i);
    },

    setZoomActive(i) {
      const n = this.zoomButtons.length;
      this.zoomIndex = i;
      this.zoomSlides.forEach((slide, k) => slide.classList.toggle('is-active', k === i));
      [i, i + 1, i - 1].forEach((k) => { if (k >= 0 && k < n) this.loadZoom(k); });
      if (i + 2 < n) this.prefetchZoom(this.zoomButtons[i + 2].dataset.mimZoom);
      if (this.zoomCount) this.zoomCount.textContent = `${i + 1} / ${n}`;
      this.zoomThumbs.forEach((t, k) => {
        t.classList.toggle('is-active', k === i);
        if (k === i) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      const active = this.zoomThumbs[i];
      if (active) {
        const row = active.closest('ul');
        const left = active.parentElement.offsetLeft - row.offsetLeft;
        if (left < row.scrollLeft || left + active.offsetWidth > row.scrollLeft + row.clientWidth) {
          row.scrollTo({ left: left - (row.clientWidth - active.offsetWidth) / 2, behavior: 'smooth' });
        }
      }
      if (this.zoomPrev) this.zoomPrev.disabled = i === 0;
      if (this.zoomNext) this.zoomNext.disabled = i === n - 1;
      // Keep the gallery on the image being viewed (so closing returns there).
      const btn = this.zoomButtons[i];
      const si = this.slides ? this.slides.indexOf(btn.closest('[data-mim-media]')) : -1;
      if (si >= 0 && si !== this.index) this.goTo(si);
      this.zoomReturn = btn;
    },

    closeZoom() {
      if (!this.overlay || this.overlay.hidden) return;
      this.overlay.hidden = true;
      document.body.classList.remove('is-drawer-open');
      if (this.zoomReturn) this.zoomReturn.focus({ preventScroll: true });
    },

    onVariantChange() {
      const chosen = Mim.utils
        .qsa('[data-mim-option-value]:checked', this.root)
        .sort((a, b) => a.dataset.mimOptionValue - b.dataset.mimOptionValue)
        .map((el) => el.value);

      const variant = this.variants.find(
        (v) => v.options.length === chosen.length && v.options.every((o, i) => o === chosen[i])
      );
      this.update(variant);
    },

    // Strike through values that have no available variant together with
    // the values chosen in the options before them (same rule as Shopify's
    // `product_option_value.available`).
    syncAvailability() {
      const inputs = Mim.utils.qsa('[data-mim-option-value]', this.root);
      if (!inputs.length || !this.variants.length) return;
      const chosen = [];
      inputs.forEach((el) => { if (el.checked) chosen[el.dataset.mimOptionValue - 1] = el.value; });
      inputs.forEach((el) => {
        const pos = el.dataset.mimOptionValue - 1;
        const available = this.variants.some((v) => v.available
          && v.options[pos] === el.value
          && v.options.slice(0, pos).every((o, i) => o === chosen[i]));
        const label = el.nextElementSibling;
        if (label) label.classList.toggle('is-unavailable', !available);
      });
    },

    update(variant) {
      this.syncAvailability();
      if (!this.atc) return;

      if (!variant) {
        this.atc.disabled = true;
        this.atcText.textContent = this.atc.dataset.textUnavailable;
        this.syncSticky();
        return;
      }

      if (this.idField) this.idField.value = variant.id;
      this.applyQtyRule(variant.qty);
      this.swapVariantViews(variant.id);
      this.loadPickup(variant.id);

      if (this.syncUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      const unitEl = this.root.querySelector('[data-mim-unit-price]');
      if (unitEl) {
        unitEl.hidden = !variant.unit_html;
        unitEl.innerHTML = variant.unit_html || '';
      }

      if (this.priceEl) {
        this.priceEl.innerHTML = variant.on_sale
          ? `<s class="mim-product__price-was">${variant.compare_html}</s> <span class="mim-product__price-now">${variant.price_html}</span>`
          : variant.price_html;
      }

      this.atc.disabled = !variant.available;
      const buyNow = this.root.querySelector('[data-mim-dynamic-checkout]');
      if (buyNow) buyNow.hidden = !variant.available;
      // A quantity picker means nothing for a sold-out variant.
      const qty = this.root.querySelector('[data-mim-qty]');
      if (qty) qty.hidden = !variant.available;
      this.atcText.textContent = variant.available
        ? this.atc.dataset.textAdd
        : this.atc.dataset.textSold;
      this.syncSticky();

      if (variant.media_id) this.showMedia(String(variant.media_id));
    },

    // Variant change → jump to that variant's media.
    showMedia(id) {
      if (!this.slides) return;
      const i = this.slides.findIndex((el) => el.dataset.mimMedia === id);
      if (i >= 0) this.goTo(i);
    },

    pauseMedia(el) {
      const video = el.querySelector('video');
      if (video && !video.paused) video.pause();
      const iframe = el.querySelector('iframe');
      if (iframe) iframe.src = iframe.src; // stops external (YouTube/Vimeo) playback
      const model = el.querySelector('model-viewer');
      const ui = model && this.modelUis && this.modelUis.get(model);
      if (ui && typeof ui.pause === 'function') ui.pause();
    },

    // ── 3D models & AR ───────────────────────────────────────────────────────
    // Shopify serves both libraries on demand (Shopify.loadFeatures):
    // `model-viewer-ui` adds the controls/poster to each <model-viewer>,
    // `shopify-xr` powers the "View in your space" (AR) button.
    initModels() {
      const viewers = Mim.utils.qsa('model-viewer', this.root);
      if (!viewers.length || !window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;

      window.Shopify.loadFeatures([{
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: (errors) => {
          if (errors || !window.Shopify.ModelViewerUI) return;
          this.modelUis = new Map();
          viewers.forEach((mv) => this.modelUis.set(mv, new window.Shopify.ModelViewerUI(mv)));
        },
      }]);

      if (this.root.querySelector('[data-mim-xr-models]')) {
        window.Shopify.loadFeatures([{
          name: 'shopify-xr',
          version: '1.0',
          onLoad: (errors) => { if (!errors) this.setupXr(); },
        }]);
      }
    },

    setupXr() {
      if (!window.ShopifyXR) {
        document.addEventListener('shopify_xr_initialized', () => this.setupXr(), { once: true });
        return;
      }
      const json = this.root.querySelector('[data-mim-xr-models]');
      if (json) {
        window.ShopifyXR.addModels(JSON.parse(json.textContent));
        json.remove();
      }
      window.ShopifyXR.setupXRElements();
    },

    // Point the AR button at the 3D model on screen (or keep the first one).
    syncAr() {
      if (!this.arButton) return;
      const slide = this.slides[this.index];
      if (slide && slide.dataset.mimMediaType === 'model') {
        this.arButton.setAttribute('data-shopify-model3d-id', slide.dataset.mimMedia);
      }
    },
  };

  Mim.ready(() => Mim.register('product', Product));
})();
